import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Lottie from 'lottie-react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Eye,
  EyeOff,
  User,
  Mail,
  Lock,
  Phone,
  MapPin,
  Shield,
  CheckCircle,
  ArrowRight,
  Award,
  DollarSign,
  Zap,
  AlertCircle,
  Loader2,
  Calendar,
  Globe
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import SwiftGoldCaptcha from './SwiftGoldCaptcha'
import CountrySelector from './CountrySelector'

export default function AuthModal({ onClose, onLogin }) {
  const { t } = useTranslation('auth')
  const { login: authLogin, register: authRegister } = useAuth()
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [signupAnimation, setSignupAnimation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [captchaVerified, setCaptchaVerified] = useState(false)
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    phone: '',
    address: '',
    ssn: '',
    dateOfBirth: '',
    country: ''
  })

  useEffect(() => {
    fetch('/animations/signup.json')
      .then(res => res.json())
      .then(data => setSignupAnimation(data))
      .catch(err => console.error('Error loading animation:', err))
  }, [])

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // For login, show captcha first if not verified
    if (isLogin && !captchaVerified) {
      setShowCaptcha(true)
      return
    }

    if (!isLogin && currentStep < 3) {
      setCurrentStep(currentStep + 1)
      return
    }

    // For signup step 3, show captcha if not verified
    if (!isLogin && currentStep === 3 && !captchaVerified) {
      setShowCaptcha(true)
      return
    }

    // Validation for signup
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError(t('errors.passwordMismatch'))
        return
      }
      if (formData.password.length < 8) {
        setError(t('errors.passwordMinLength'))
        return
      }
    }

    setLoading(true)

    try {
      if (isLogin) {
        // Login using AuthContext
        const result = await authLogin(formData.email, formData.password)
        if (result.success) {
          onLogin(result.user)
        } else {
          // Map backend error messages to translated messages
          const errorMessage = result.error || ''
          if (errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('email')) {
            setError(t('errors.invalidCredentials'))
          } else if (errorMessage.toLowerCase().includes('locked')) {
            setError(t('errors.accountLocked'))
          } else if (errorMessage.toLowerCase().includes('inactive') || errorMessage.toLowerCase().includes('suspended')) {
            setError(t('errors.accountInactive'))
          } else {
            setError(errorMessage || t('errors.loginFailed'))
          }
          // Reset captcha so user can try again
          setCaptchaVerified(false)
        }
      } else {
        // Register using AuthContext
        const registerData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          country: formData.country || undefined
        }

        const result = await authRegister(registerData)
        if (result.success) {
          onLogin(result.user)
        } else {
          // Map backend error messages to translated messages for registration
          const errorMessage = result.error || ''
          if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('registered')) {
            setError(t('errors.emailExists'))
          } else {
            setError(errorMessage || t('errors.registrationFailed'))
          }
          // Reset captcha so user can try again
          setCaptchaVerified(false)
        }
      }
    } catch (err) {
      console.error('Auth error:', err)
      // Handle network errors
      if (err.message?.toLowerCase().includes('network') || err.message?.toLowerCase().includes('fetch')) {
        setError(t('errors.networkError'))
      } else {
        setError(err.message || t('errors.genericError'))
      }
      // Reset captcha on error
      setCaptchaVerified(false)
    } finally {
      setLoading(false)
    }
  }

  const handleCaptchaVerified = () => {
    setCaptchaVerified(true)
    setShowCaptcha(false)
    // Auto-submit the form after captcha verification
    setTimeout(() => {
      document.getElementById('auth-form')?.requestSubmit()
    }, 500)
  }

  const handleCaptchaFailed = () => {
    setError(t('errors.captchaFailed'))
    setShowCaptcha(false)
  }

  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: t('benefits.security.title'),
      description: t('benefits.security.description')
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: t('benefits.noFees.title'),
      description: t('benefits.noFees.description')
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: t('benefits.instantAccess.title'),
      description: t('benefits.instantAccess.description')
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: t('benefits.highApy.title'),
      description: t('benefits.highApy.description')
    }
  ]

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 md:p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white w-full max-w-7xl rounded-none md:rounded-3xl shadow-2xl overflow-hidden h-full md:max-h-[95vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-lg md:rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
                    {isLogin ? t('login.title') : t('register.title')}
                  </h2>
                  <p className="text-amber-100 text-xs md:text-sm">
                    {isLogin ? t('login.subtitle') : t('register.subtitle')}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-7 h-7 text-white" />
              </button>
            </div>

            {/* Progress Steps for Signup */}
            {!isLogin && (
              <div className="mt-6 flex items-center gap-2">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex-1 flex items-center gap-2">
                    <div className={`flex-1 h-2 rounded-full transition-all ${
                      step <= currentStep ? 'bg-white' : 'bg-white/30'
                    }`} />
                    {step < 3 && <div className="w-2 h-2 rounded-full bg-white/30" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid lg:grid-cols-2 min-h-full">
              {/* Left Side - Form */}
              <div className="p-8 lg:p-12">
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, x: 0 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      x: [0, -10, 10, -10, 10, 0]
                    }}
                    transition={{
                      x: { duration: 0.5, ease: "easeInOut" }
                    }}
                    className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl flex items-center gap-3 shadow-sm"
                  >
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <p className="text-red-700 font-medium">{error}</p>
                  </motion.div>
                )}

                {/* Captcha Modal */}
                <AnimatePresence>
                  {showCaptcha && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
                      onClick={() => setShowCaptcha(false)}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SwiftGoldCaptcha
                          onVerified={handleCaptchaVerified}
                          onFailed={handleCaptchaFailed}
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {isLogin ? (
                  // Login Form
                  <div className="max-w-md mx-auto">
                    <form id="auth-form" onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t('login.email')}
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                          <input
                            type="email"
                            name="email"
                            placeholder="your@email.com"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {t('login.password')}
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder={t('login.passwordPlaceholder')}
                            value={formData.password}
                            onChange={handleInputChange}
                            className="w-full pl-12 pr-14 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                            required
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2">
                          <input type="checkbox" className="w-5 h-5 text-amber-600 border-slate-300 rounded focus:ring-amber-500" />
                          <span className="text-slate-600 font-medium">{t('login.rememberMe')}</span>
                        </label>
                        <a href="#" className="text-amber-600 hover:text-amber-700 font-semibold text-sm">
                          {t('login.forgotPassword')}
                        </a>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-bold text-lg transition-all hover:shadow-xl hover:shadow-amber-200/50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {t('login.signingIn')}
                          </>
                        ) : (
                          <>
                            {t('login.signIn')}
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </form>

                    <div className="mt-8 text-center">
                      <p className="text-slate-600">
                        {t('login.noAccount')}{' '}
                        <button
                          onClick={() => { setIsLogin(false); setError(null); setCaptchaVerified(false); }}
                          className="text-amber-600 hover:text-amber-700 font-bold"
                        >
                          {t('login.signUpFree')}
                        </button>
                      </p>
                    </div>
                  </div>
                ) : (
                  // Signup Form
                  <div className="max-w-md mx-auto">
                    <div className="mb-8">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">
                        {currentStep === 1 && t('register.steps.personal.title')}
                        {currentStep === 2 && t('register.steps.contact.title')}
                        {currentStep === 3 && t('register.steps.security.title')}
                      </h3>
                      <p className="text-slate-600">
                        {t('register.stepOf', { current: currentStep, total: 3 })} - {currentStep === 1 && t('register.steps.personal.subtitle')}
                        {currentStep === 2 && t('register.steps.contact.subtitle')}
                        {currentStep === 3 && t('register.steps.security.subtitle')}
                      </p>
                    </div>

                    <form id="auth-form" onSubmit={handleSubmit} className="space-y-6">
                      {currentStep === 1 && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 mb-2">
                                {t('register.firstName')}
                              </label>
                              <div className="relative">
                                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                                <input
                                  type="text"
                                  name="firstName"
                                  placeholder="John"
                                  value={formData.firstName}
                                  onChange={handleInputChange}
                                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                  required
                                  disabled={loading}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-slate-700 mb-2">
                                {t('register.lastName')}
                              </label>
                              <input
                                type="text"
                                name="lastName"
                                placeholder="Doe"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                className="w-full px-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                required
                                disabled={loading}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.email')}
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                              <input
                                type="email"
                                name="email"
                                placeholder={t('register.emailPlaceholder')}
                                value={formData.email}
                                onChange={handleInputChange}
                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                required
                                disabled={loading}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.dateOfBirth', 'Date of Birth')}
                            </label>
                            <div className="relative">
                              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                              <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:bg-white transition-all text-lg"
                                required
                                disabled={loading}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.country', 'Country')}
                            </label>
                            <CountrySelector
                              value={formData.country}
                              onChange={handleInputChange}
                              disabled={loading}
                              required
                              placeholder={t('register.selectCountry', 'Select your country')}
                            />
                          </div>
                        </>
                      )}

                      {currentStep === 2 && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.phone')}
                            </label>
                            <div className="relative">
                              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                              <input
                                type="tel"
                                name="phone"
                                placeholder="+1 (555) 000-0000"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                disabled={loading}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.addressOptional')}
                            </label>
                            <div className="relative">
                              <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                              <input
                                type="text"
                                name="address"
                                placeholder="123 Main St, New York, NY 10001"
                                value={formData.address}
                                onChange={handleInputChange}
                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                disabled={loading}
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {currentStep === 3 && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.createPassword')}
                            </label>
                            <div className="relative">
                              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                              <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder={t('register.passwordPlaceholder')}
                                value={formData.password}
                                onChange={handleInputChange}
                                className="w-full pl-12 pr-14 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                required
                                disabled={loading}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                              >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                            </div>
                            <div className="mt-2 space-y-1">
                              <div className={`flex items-center gap-2 text-xs ${formData.password.length >= 8 ? 'text-green-600' : 'text-slate-400'}`}>
                                <CheckCircle className={`w-4 h-4 ${formData.password.length >= 8 ? 'text-green-500' : 'text-slate-300'}`} />
                                {t('register.passwordRequirements.minLength')}
                              </div>
                              <div className={`flex items-center gap-2 text-xs ${/[0-9]/.test(formData.password) && /[^a-zA-Z0-9]/.test(formData.password) ? 'text-green-600' : 'text-slate-400'}`}>
                                <CheckCircle className={`w-4 h-4 ${/[0-9]/.test(formData.password) && /[^a-zA-Z0-9]/.test(formData.password) ? 'text-green-500' : 'text-slate-300'}`} />
                                {t('register.passwordRequirements.numberAndSpecial')}
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {t('register.confirmPassword')}
                            </label>
                            <div className="relative">
                              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                              <input
                                type="password"
                                name="confirmPassword"
                                placeholder={t('register.confirmPasswordPlaceholder')}
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-lg"
                                required
                                disabled={loading}
                              />
                            </div>
                            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                              <p className="mt-2 text-xs text-red-500">{t('errors.passwordMismatch')}</p>
                            )}
                          </div>

                          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input type="checkbox" className="w-5 h-5 text-amber-600 border-slate-300 rounded mt-1" required />
                              <div className="text-sm text-slate-700">
                                {t('register.agreeToTerms')}{' '}
                                <a href="#" className="text-amber-600 hover:text-amber-700 font-semibold">{t('register.termsOfService')}</a>
                                {' '}{t('common:and')}{' '}
                                <a href="#" className="text-amber-600 hover:text-amber-700 font-semibold">{t('register.privacyPolicy')}</a>
                              </div>
                            </label>
                          </div>
                        </>
                      )}

                      <div className="flex gap-3">
                        {currentStep > 1 && (
                          <button
                            type="button"
                            onClick={() => setCurrentStep(currentStep - 1)}
                            disabled={loading}
                            className="flex-1 py-4 border-2 border-slate-300 hover:border-slate-400 text-slate-700 rounded-xl font-bold text-lg transition-all disabled:opacity-50"
                          >
                            {t('common:back')}
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-bold text-lg transition-all hover:shadow-xl hover:shadow-amber-200/50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              {currentStep === 3 ? t('register.creatingAccount') : t('common:processing')}
                            </>
                          ) : (
                            <>
                              {currentStep === 3 ? t('register.createAccount') : t('common:continue')}
                              <ArrowRight className="w-5 h-5" />
                            </>
                          )}
                        </button>
                      </div>
                    </form>

                    <div className="mt-6 text-center">
                      <p className="text-slate-600">
                        {t('register.alreadyHaveAccount')}{' '}
                        <button
                          onClick={() => { setIsLogin(true); setCurrentStep(1); setError(null); setCaptchaVerified(false); }}
                          className="text-amber-600 hover:text-amber-700 font-bold"
                        >
                          {t('login.signIn')}
                        </button>
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Benefits & Animation */}
              <div className="hidden lg:block bg-gradient-to-br from-blue-50 to-cyan-50 p-12 relative overflow-hidden">
                <div className="relative z-10 h-full flex flex-col justify-center">
                  {/* Animation */}
                  {signupAnimation && (
                    <div className="mb-8">
                      <Lottie animationData={signupAnimation} loop={true} style={{ width: '100%', height: 'auto', maxHeight: '300px' }} />
                    </div>
                  )}

                  {/* Benefits */}
                  <div className="space-y-6">
                    <h3 className="text-3xl font-bold text-slate-900 mb-6">
                      {isLogin ? t('benefits.loginTitle') : t('benefits.registerTitle')}
                    </h3>

                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.5 }}
                        className="flex gap-4 items-start bg-white p-5 rounded-2xl shadow-sm border-2 border-blue-100 hover:border-blue-300 transition-all"
                      >
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white">
                          {benefit.icon}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-1">{benefit.title}</h4>
                          <p className="text-slate-600 text-sm">{benefit.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-8 pt-8 border-t-2 border-blue-200">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-3xl font-bold text-amber-600">2.5M+</div>
                        <div className="text-xs text-slate-600">{t('stats.users')}</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-amber-600">$15B+</div>
                        <div className="text-xs text-slate-600">{t('stats.assets')}</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-amber-600">99.99%</div>
                        <div className="text-xs text-slate-600">{t('stats.uptime')}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-200 rounded-full filter blur-3xl opacity-30" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-200 rounded-full filter blur-3xl opacity-30" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
